<template>
    <el-input
      :value="computedInputValue"
      @input="onInput($event)"
      @change="onChange($event)"
      :min="min || undefined"
      :max="max || undefined"
      :required="!!required"
      :disabled="!!disabled"
      :placeholder="placeholder"
      autocomplete="off">
      <template v-slot:prepend>$</template>
    </el-input>
</template>

<script>
import { debounce } from 'lodash';

export default {
  name: 'ElPriceInput',
  props: {
    value: Number,
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    placeholder: String,
    min: { type: Number, default: 0 },
    max: { type: Number, default: Infinity },
  },
  data() {
    return {
      inputValue: '',
    };
  },
  mounted() {
    if (this.value !== undefined) this.inputValue = this.value / 100;
  },
  methods: {
    onInput(val) {
      if (/^[0-9]*\.?[0-9]{0,2}$/.test(val)) {
        this.inputValue = /^[0]+$/.test(val) ? 0 : val;
      }
    },
    onChange(val) {
      this.updatePrice(val);
    },
    updatePrice: debounce(function updatePrice() {
      if (this.inputValue * 100 >= this.min && this.inputValue * 100 <= this.max) {
        this.$emit('input', parseFloat(this.inputValue) * 100);
      } else if (this.inputValue.length || this.inputValue === 0) {
        this.$message.error(`Price outside allowed range - $${this.min / 100} to $${this.max / 100}`);
        this.inputValue = this.min / 100;
        this.$emit('input', parseFloat(this.min));
      }
    }, 200),
  },
  computed: {
    computedInputValue: {
      get() {
        return this.disabled ? parseFloat(this.inputValue).toFixed(2) : this.inputValue;
      }
    }
  },
  watch: {
    value(newVal) {
      // Keep the internal inputValue in sync with external v-model updates
      if (newVal === undefined || newVal === null || Number.isNaN(newVal)) {
        this.inputValue = '';
      } else if (typeof newVal === 'number') {
        const cents = Number(newVal);
        // If incoming value exceeds max, clamp visually to max but still reflect model
        const clamped = Math.min(cents, this.max);
        this.inputValue = clamped / 100;
        // If clamped, emit back the corrected value to parent so model stays valid
        if (clamped !== cents) this.$emit('input', clamped);
      }
    },
    max(newMax) {
      // If the maximum lowers below current value, clamp displayed value
      const currentCents = Number(this.inputValue) * 100;
      if (typeof newMax === 'number' && currentCents > newMax) {
        this.inputValue = newMax / 100;
        this.$emit('input', newMax);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
::v-deep input[type=number] {
  -moz-appearance: textfield;
}
</style>
